import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Sparkles, Tag } from "lucide-react";
import { z } from "zod";

const emailSchema = z.string().trim().email("Invalid email address");
const passwordSchema = z.string().min(8, "Password must be at least 8 characters")
  .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
  .regex(/[a-z]/, "Password must contain at least one lowercase letter")
  .regex(/[0-9]/, "Password must contain at least one number");
const nameSchema = z.string().trim().min(2, "Name must be at least 2 characters").max(100, "Name too long");

const SignUp = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("invite");
  const [loading, setLoading] = useState(false);
  
  // Form fields
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [referralCode, setReferralCode] = useState(searchParams.get("ref") || "");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteAccessLevel, setInviteAccessLevel] = useState("");

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load invite data if token present
  useEffect(() => {
    if (token) {
      validateInviteToken(token);
    }
  }, [token]);

  const validateInviteToken = async (inviteToken: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("user_invites")
        .select("email, access_level, status, expires_at")
        .eq("invite_token", inviteToken)
        .maybeSingle();

      if (error || !data) {
        toast({
          title: "Invalid Invite",
          description: "This invitation link is not valid",
          variant: "destructive",
        });
        return;
      }

      if (data.status !== 'pending') {
        toast({
          title: "Invite Already Used",
          description: "This invitation has already been accepted",
          variant: "destructive",
        });
        return;
      }

      if (new Date(data.expires_at) < new Date()) {
        toast({
          title: "Invite Expired",
          description: "This invitation has expired",
          variant: "destructive",
        });
        return;
      }

      setInviteEmail(data.email);
      setEmail(data.email);
      setInviteAccessLevel(data.access_level);
      
      toast({
        title: "Invitation Valid",
        description: `Complete your registration to join with ${data.access_level.replace('_', ' ')} access`,
      });
    } catch (err: any) {
      console.error("Error validating invite:", err);
      toast({
        title: "Error",
        description: "Failed to validate invitation",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    try {
      nameSchema.parse(fullName);
    } catch (e: any) {
      newErrors.fullName = e.errors[0].message;
    }

    try {
      emailSchema.parse(email);
    } catch (e: any) {
      newErrors.email = e.errors[0].message;
    }

    try {
      passwordSchema.parse(password);
    } catch (e: any) {
      newErrors.password = e.errors[0].message;
    }

    if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors in the form",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      // Validate referral code if provided
      if (referralCode.trim()) {
        const { data: codeData, error: codeError } = await supabase
          .from("referral_codes")
          .select("code")
          .eq("code", referralCode.trim().toUpperCase())
          .maybeSingle();

        if (codeError || !codeData) {
          toast({
            title: "Invalid Referral Code",
            description: "The referral code you entered is not valid",
            variant: "destructive",
          });
          setLoading(false);
          return;
        }
      }

      // Create account
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/payment-gate`,
          data: {
            full_name: fullName.trim(),
            referred_by_code: referralCode.trim().toUpperCase() || null,
            access_level: inviteAccessLevel || null,
            invite_token: token || null,
          }
        },
      });

      if (signUpError) throw signUpError;

      if (authData.user) {
        // If using invite, mark it as accepted
        if (token) {
          await supabase
            .from("user_invites")
            .update({ 
              status: "accepted",
              accepted_at: new Date().toISOString(),
              user_id: authData.user.id
            })
            .eq("invite_token", token);
        }

        toast({
          title: "Account Created!",
          description: token ? "Invitation accepted! Complete payment to activate." : "Please complete payment to activate your account",
        });
        
        // Redirect to payment page
        navigate("/payment-gate");
      }
    } catch (error: any) {
      console.error("Sign up error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to create account",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-background via-primary/5 to-accent/5" />
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[128px]" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/20 rounded-full blur-[128px]" />

      <div className="w-full max-w-md relative z-10">
        <Button
          variant="ghost"
          onClick={() => navigate("/")}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to home
        </Button>

        <Card className="border-primary/20">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center mb-4">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <CardTitle className="text-3xl">
              {token ? "Complete Your Invitation" : "Create Your Account"}
            </CardTitle>
            <CardDescription>
              {token 
                ? `Join Launch To Revenue with ${inviteAccessLevel.replace('_', ' ')} access`
                : "Join Launch To Revenue and start building your profitable business"
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSignUp} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name *</Label>
                <Input
                  id="fullName"
                  type="text"
                  placeholder="John Doe"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className={errors.fullName ? "border-destructive" : ""}
                  required
                />
                {errors.fullName && (
                  <p className="text-xs text-destructive">{errors.fullName}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={errors.email ? "border-destructive" : ""}
                  disabled={!!inviteEmail || loading}
                  required
                />
                {inviteEmail && (
                  <p className="text-xs text-muted-foreground">
                    Email pre-filled from invitation
                  </p>
                )}
                {errors.email && (
                  <p className="text-xs text-destructive">{errors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter secure password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={errors.password ? "border-destructive" : ""}
                  required
                />
                {errors.password && (
                  <p className="text-xs text-destructive">{errors.password}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Must be 8+ characters with uppercase, lowercase, and number
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password *</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={errors.confirmPassword ? "border-destructive" : ""}
                  required
                />
                {errors.confirmPassword && (
                  <p className="text-xs text-destructive">{errors.confirmPassword}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="referralCode" className="flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  Referral Code (Optional)
                </Label>
                <Input
                  id="referralCode"
                  type="text"
                  placeholder="Enter referral code"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                  className="uppercase"
                  disabled={!!token || loading}
                />
                {token ? (
                  <p className="text-xs text-muted-foreground">
                    Using invitation link (referral code disabled)
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Have a referral code? Enter it here to track your referrer
                  </p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={loading}
              >
                {loading ? "Creating Account..." : "Create Account & Continue to Payment"}
              </Button>

              <div className="text-center text-sm">
                <p className="text-muted-foreground">
                  Already have an account?{" "}
                  <button
                    type="button"
                    onClick={() => navigate("/sign-in")}
                    className="text-primary hover:underline font-medium"
                  >
                    Sign In
                  </button>
                </p>
              </div>

              <div className="text-xs text-center text-muted-foreground pt-4">
                By creating an account, you agree to payment of $200 for lifetime access
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SignUp;